import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, FileText, Download, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_COLORS = {
  draft: { bg: "#F5F3EF", text: "#57534E" },
  sent: { bg: "#DBEAFE", text: "#1D4ED8" },
  paid: { bg: "#D1FAE5", text: "#059669" },
  overdue: { bg: "#FEE2E2", text: "#DC2626" },
};

export default function Invoices() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [filterClient, setFilterClient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [form, setForm] = useState({
    client_id: "", billing_cycle_id: "", due_date: "", tax_rate: "0", notes: "",
    autoGenerate: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const cycleParam = searchParams.get("cycle");
    if (cycleParam) {
      api.get(`/billing/cycles/${cycleParam}`).then((r) => {
        setForm((prev) => ({ ...prev, client_id: r.data.client_id, billing_cycle_id: cycleParam }));
        setShowDialog(true);
      });
    }
  }, []);

  useEffect(() => {
    Promise.all([api.get("/invoices"), api.get("/clients"), api.get("/billing/cycles")]).then(([inv, cl, cy]) => {
      setInvoices(inv.data);
      setClients(cl.data);
      setCycles(cy.data.filter((c) => c.status === "closed"));
    }).finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let r;
      if (form.autoGenerate && form.billing_cycle_id) {
        r = await api.post(`/invoices/generate/${form.billing_cycle_id}?due_date=${form.due_date}&tax_rate=${form.tax_rate || 0}`);
      } else {
        r = await api.post("/invoices", {
          client_id: form.client_id,
          billing_cycle_id: form.billing_cycle_id || null,
          due_date: form.due_date,
          tax_rate: parseFloat(form.tax_rate) || 0,
          notes: form.notes,
          line_items: [],
        });
      }
      setInvoices((prev) => [r.data, ...prev]);
      setShowDialog(false);
      toast.success("Invoice created");
      navigate(`/invoices/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (id) => {
    try {
      await api.post(`/invoices/${id}/send`);
      setInvoices((prev) => prev.map((inv) => inv.id === id ? { ...inv, status: "sent" } : inv));
      toast.success("Invoice sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invoice");
    }
  };

  const handleDownloadPDF = async (id, invNum) => {
    try {
      const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invNum}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const filtered = invoices.filter((inv) => {
    const matchClient = !filterClient || inv.client_id === filterClient;
    const matchStatus = !filterStatus || inv.status === filterStatus;
    return matchClient && matchStatus;
  });

  const clientCycles = cycles.filter((c) => !form.client_id || c.client_id === form.client_id);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="invoices-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>Invoices</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>{invoices.length} invoice{invoices.length !== 1 ? "s" : ""}</p>
          </div>
          <Button data-testid="create-invoice-button" onClick={() => setShowDialog(true)} style={{ background: "#292524", color: "#fff" }} className="flex items-center gap-2">
            <Plus size={15} />New Invoice
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}>
            <option value="">All Clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
          {loading ? (
            <div className="py-12 text-center text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center">
              <FileText size={32} className="mx-auto mb-3" style={{ color: "#E7E5E4" }} />
              <p className="text-sm" style={{ color: "#A8A29E" }}>No invoices found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: "1px solid #E7E5E4" }}>
                  {["Invoice #", "Client", "Amount", "Status", "Due", ""].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => {
                  const sc = STATUS_COLORS[inv.status] || { bg: "#F5F3EF", text: "#57534E" };
                  return (
                    <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}
                      onClick={() => navigate(`/invoices/${inv.id}`)}
                      className="border-b hover:bg-stone-50 transition-colors cursor-pointer"
                      style={{ borderColor: "#F5F3EF" }}
                    >
                      <td className="px-5 py-4 text-sm font-medium" style={{ color: "#292524" }}>{inv.invoice_number}</td>
                      <td className="px-5 py-4 text-sm" style={{ color: "#57534E" }}>{inv.client_name}</td>
                      <td className="px-5 py-4 text-sm font-semibold font-serif" style={{ color: "#292524" }}>£{inv.total?.toFixed(2)}</td>
                      <td className="px-5 py-4">
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>{inv.status}</span>
                      </td>
                      <td className="px-5 py-4 text-sm" style={{ color: "#A8A29E" }}>{inv.due_date}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDownloadPDF(inv.id, inv.invoice_number); }}
                            className="p-1.5 rounded hover:bg-stone-100"
                            style={{ color: "#57534E" }}
                            title="Download PDF"
                            data-testid={`download-pdf-${inv.id}`}
                          >
                            <Download size={14} />
                          </button>
                          {inv.status === "draft" && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSend(inv.id); }}
                              className="p-1.5 rounded hover:bg-blue-50"
                              style={{ color: "#0284C7" }}
                              title="Send"
                              data-testid={`send-invoice-${inv.id}`}
                            >
                              <Send size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="create-invoice-dialog">
          <DialogHeader><DialogTitle className="font-serif">New Invoice</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label>Client *</Label>
              <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value, billing_cycle_id: "" })}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E7E5E4", background: "#fff" }}>
                <option value="">Select client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Billing Cycle (optional)</Label>
              <select value={form.billing_cycle_id} onChange={(e) => setForm({ ...form, billing_cycle_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E7E5E4", background: "#fff" }}>
                <option value="">None (manual entry)</option>
                {clientCycles.map((c) => <option key={c.id} value={c.id}>{c.start_date?.slice(0, 10)} — {c.end_date?.slice(0, 10)}</option>)}
              </select>
            </div>
            {form.billing_cycle_id && (
              <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: "#FEF3C7" }}>
                <input type="checkbox" id="auto-gen" checked={form.autoGenerate} onChange={(e) => setForm({ ...form, autoGenerate: e.target.checked })} />
                <Label htmlFor="auto-gen" className="text-sm" style={{ color: "#D97706" }}>
                  Auto-generate line items from billing cycle
                </Label>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due Date *</Label><Input type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div><Label>Tax Rate (%)</Label><Input type="number" min="0" max="100" step="0.1" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} placeholder="0" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button data-testid="create-invoice-submit" type="submit" disabled={saving} style={{ background: "#292524", color: "#fff" }}>
                {saving ? "Creating..." : "Create Invoice"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
